/**
 * The entry window's Agents and History tabs, the refusal when someone else saved
 * first, and the words for what a change did to agents.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import { useTenantPath } from '@/routing';
import { formatExactTime, formatRelativeTime } from '@/utils/relativeTime';
import { ConnectorDialog, PrimaryAction } from '@modules/chatbots/components/ConnectorDialog';
import {
  connectorLibraryService,
  type AgentUsingEntry,
  type ChangeOutcome,
  type LibraryChange,
} from '../services/connectorLibraryService';

type T = (key: string, options?: Record<string, unknown>) => string;

function Loading() {
  return <div className="cxl-loading" aria-busy="true"><span className="loading-spinner" /></div>;
}

/** "12 agents updated · 2 reset" — what a change did, for the toast after it. */
export function outcomeText(t: T, outcome: ChangeOutcome | null | undefined): string {
  if (!outcome) return '';
  const parts = [
    outcome.updated ? t('connectorLibrary.outcome.updated', { count: outcome.updated }) : '',
    outcome.reset ? t('connectorLibrary.outcome.reset', { count: outcome.reset }) : '',
    outcome.detached ? t('connectorLibrary.outcome.detached', { count: outcome.detached }) : '',
    outcome.keys_needed ? t('connectorLibrary.outcome.keysNeeded', { count: outcome.keys_needed }) : '',
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : t('connectorLibrary.outcome.none');
}

/** "Last changed by Dana, 2 hours ago" — the person when the viewer may know, else the organization. */
export function lastChangeText(t: T, change: LibraryChange | null | undefined): string | null {
  if (!change) return null;
  const who = change.by || change.organization;
  const when = formatRelativeTime(change.at ?? undefined);
  return who ? t('connectorLibrary.lastChange', { name: who, when }) : t('connectorLibrary.lastChangeAnonymous', { when });
}

// ---------------------------------------------------------------------------
// Agents using an entry
// ---------------------------------------------------------------------------

export function AgentsPanel({ entryId, tenantId }: { entryId: string; tenantId: string }) {
  const { t } = useLanguage();
  const tenantPath = useTenantPath();
  const [agents, setAgents] = useState<AgentUsingEntry[] | null>(null);
  const [failed, setFailed] = useState(false);

  // Keyed on the entry alone: `t` is not a stable reference.
  useEffect(() => {
    let live = true;
    setAgents(null);
    setFailed(false);
    connectorLibraryService.agentsUsing(entryId, tenantId)
      .then(a => { if (live) setAgents(a); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [entryId, tenantId]);

  // By organization, then by status — the order the server sends within each.
  const byOrg = useMemo(() => {
    const map = new Map<string, AgentUsingEntry[]>();
    (agents ?? []).forEach(a => map.set(a.tenant_name, [...(map.get(a.tenant_name) ?? []), a]));
    return [...map.entries()];
  }, [agents]);

  if (failed) return <p className="cxl-panel__error" role="alert">{t('connectorLibrary.errors.loadAgents')}</p>;
  if (!agents) return <Loading />;
  if (agents.length === 0) return <p className="cxl-panel__empty">{t('connectorLibrary.agents.none')}</p>;
  return (
    <div className="cxl-agents" data-testid="library-agents-list">
      <p className="cxl-panel__lead">{t('connectorLibrary.agents.subtitle')}</p>
      {byOrg.map(([org, rows]) => (
        <section key={org} className="cxl-agents__org-block">
          <h4 className="cxl-agents__org"><bdi dir="auto">{org}</bdi></h4>
          <ul className="cxl-agents__rows">
            {rows.map(agent => (
              <li key={agent.chatbot_id} className="cxl-agents__row" data-testid={`library-agent-${agent.chatbot_id}`}>
                {/* Opens that agent's Connectors tab in a new tab, so the list stays
                    where it was; rel="opener" (same origin) carries the sign-in, which
                    lives in sessionStorage. */}
                <a className="cxl-agents__name" href={tenantPath(`/agents?edit=${agent.chatbot_id}&tab=connectors`)}
                   target="_blank" rel="opener" title={t('connectorLibrary.agents.open')}
                   data-testid={`library-agent-open-${agent.chatbot_id}`}>
                  <bdi dir="auto">{agent.chatbot_name}</bdi>
                </a>
                <span className={`cx-pill ${agent.state === 'following' ? 'cx-pill--accent' : 'cxl-pill--customized'}`}
                      title={agent.state === 'following' ? t('connectorLibrary.status.sharedTip') : t('connectorLibrary.status.customizedTip')}>
                  {agent.state === 'following' ? t('connectorLibrary.status.sharedShort') : t('connectorLibrary.status.customized')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

export function HistoryPanel({ entryId, tenantId }: { entryId: string; tenantId: string }) {
  const { t } = useLanguage();
  const [history, setHistory] = useState<LibraryChange[] | null>(null);
  const [failed, setFailed] = useState(false);

  // Keyed on the entry alone: `t` is not a stable reference.
  useEffect(() => {
    let live = true;
    setHistory(null);
    setFailed(false);
    connectorLibraryService.history(entryId, tenantId)
      .then(h => { if (live) setHistory(h); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [entryId, tenantId]);

  if (failed) return <p className="cxl-panel__error" role="alert">{t('connectorLibrary.errors.loadHistory')}</p>;
  if (!history) return <Loading />;
  if (history.length === 0) return <p className="cxl-panel__empty">{t('connectorLibrary.history.none')}</p>;
  return (
    <>
      <p className="cxl-panel__lead">{t('connectorLibrary.history.subtitle')}</p>
      <ol className="cxl-history" data-testid="library-history-list">
        {history.map((change, i) => {
          const reached = outcomeText(t, {
            updated: change.detail.updated ?? 0, reset: change.detail.reset ?? 0,
            detached: change.detail.detached ?? 0, keys_needed: change.detail.keys_needed ?? 0,
          });
          const touchedAgents = !!(change.detail.updated || change.detail.reset
            || change.detail.detached || change.detail.keys_needed);
          return (
            <li key={`${change.at}-${i}`} className="cxl-history__row">
              <span className="cxl-history__head">
                <span className={`cxl-history__action cxl-history__action--${change.action}`}>
                  {t(`connectorLibrary.history.actions.${change.action}`)}
                </span>
                <span className="cxl-history__who"><bdi dir="auto">{change.by || change.organization}</bdi></span>
                <time className="cxl-history__when" dateTime={change.at ?? undefined} title={formatExactTime(change.at ?? undefined)}>
                  {formatRelativeTime(change.at ?? undefined)}
                </time>
              </span>
              {change.action === 'acknowledged' && (
                <span className="cxl-history__detail" data-testid="library-history-ack">
                  {change.detail.risk === 'write'
                    ? <>{t('connectorLibrary.history.ackWrite')} <bdi dir="ltr">{(change.detail.tools ?? []).join(', ')}</bdi></>
                    : t('connectorLibrary.history.ackCustom')}
                </span>
              )}
              {(touchedAgents || change.detail.force) && (
                <span className="cxl-history__detail">
                  {touchedAgents ? reached : ''}
                  {change.detail.force ? `${touchedAgents ? ' · ' : ''}${t('connectorLibrary.history.forced')}` : ''}
                </span>
              )}
              {(fieldNames(t, change.detail.fields).length > 0 || (change.by && change.organization)) && (
                <span className="cxl-history__org">
                  {fieldNames(t, change.detail.fields).length > 0 && (
                    <span>{t('connectorLibrary.history.fields', { fields: fieldNames(t, change.detail.fields).join(', ') })}</span>
                  )}
                  {fieldNames(t, change.detail.fields).length > 0 && change.by && change.organization && ' · '}
                  {change.by && change.organization && (
                    <span>{t('connectorLibrary.history.forOrganization')} <bdi dir="auto">{change.organization}</bdi></span>
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </>
  );
}

/** "name, description" — the fields a change touched, in words. A field this
    page has no word for is left out rather than shown by its code name. */
function fieldNames(t: T, fields: string[] | undefined): string[] {
  const known: Record<string, string> = {
    name: 'connectorLibrary.history.field.name',
    description: 'connectorLibrary.history.field.description',
    disabled_tools: 'connectorLibrary.history.field.tools',
    operation_settings: 'connectorLibrary.history.field.actions',
    instructions: 'connectorLibrary.history.field.guide',
    cache_ttl_seconds: 'connectorLibrary.history.field.cache',
    max_result_chars: 'connectorLibrary.history.field.resultSize',
    url: 'connectorLibrary.history.field.address',
    base_url: 'connectorLibrary.history.field.address',
    spec: 'connectorLibrary.history.field.document',
    spec_url: 'connectorLibrary.history.field.document',
    spec_title: 'connectorLibrary.history.field.document',
    spec_version: 'connectorLibrary.history.field.document',
    operations: 'connectorLibrary.history.field.actions',
    tools: 'connectorLibrary.history.field.tools',
    credential: 'connectorLibrary.history.field.credential',
    share_credential: 'connectorLibrary.history.field.credential',
    auth: 'connectorLibrary.history.field.credential',
    auth_type: 'connectorLibrary.history.field.credential',
    auth_meta: 'connectorLibrary.history.field.credential',
    auth_header_name: 'connectorLibrary.history.field.credential',
  };
  return [...new Set((fields ?? []).map(f => known[f]).filter(Boolean))].map(key => t(key));
}

// ---------------------------------------------------------------------------
// Someone else saved first
// ---------------------------------------------------------------------------

export function ConflictDialog({ changedBy, changedAt, changedAbove, onClose, onReload }: {
  changedBy: string | null | undefined; changedAt: string | null | undefined; changedAbove?: boolean;
  onClose: () => void; onReload: () => void;
}) {
  const { t } = useLanguage();
  const time = formatExactTime(changedAt ?? undefined);
  return (
    <ConnectorDialog
      size="medium"
      title={t('connectorLibrary.conflict.title')}
      onClose={onClose}
      testId="library-version-conflict"
      actions={(<>
        <button type="button" className="btn-cancel" onClick={onClose}>{t('common.close')}</button>
        <PrimaryAction label={t('connectorLibrary.conflict.reload')} onClick={onReload} testId="library-version-reload" />
      </>)}
    >
      <p className="cxl-conflict__text" role="alert">
        {changedAbove ? t('connectorLibrary.conflict.bodyAbove')
          : changedBy ? t('connectorLibrary.conflict.body', { name: changedBy, time })
            : t('connectorLibrary.conflict.bodyAnonymous', { time })}
      </p>
      <p className="cxl-conflict__hint">{t('connectorLibrary.conflict.hint')}</p>
    </ConnectorDialog>
  );
}
